// Adjust stock (UC-INVENTORY-006; stock.html's Adjust with the lock mark): one reason — Damaged, Lost or theft, Correction, Display
// or sample, Returned to supplier or Other — a note, and lines added by a scan or a typed SKU, serial or barcode (a scanner's code
// that lands in a line's Change or Unit cost taken as a scan, never posting the dialog; a tablet's camera reading one): a serial's line
// changes by 1 and says what becomes of it (written off, kept as damaged, released, returned to the supplier, corrected), a
// quantity line takes a whole change, an increase its unit cost ex VAT pre-filled with the branch's weighted average. Each line's
// value at cost and the signed total; a document that lowers stock says it needs the Control PIN (`StockAdjustment`, one for the
// whole document) and the note is its Control Log reason. The stock refusals — more than is in store, a serial not available —
// come back before any PIN and are shown under their lines. And the branch's adjustments (A11): the list, newest first, and one
// adjustment with its lines.
import {
  ADJUSTMENT_REASONS, STOCK_MOVEMENT_REASON_LABELS, cents, type Cents, type AdjustmentLineResult, type AdjustmentReason, type AdjustmentView, type ProductSummary, type SerialStatus,
  type StockLineView,
} from "@visionx/contracts";
import { sumCents, timesQuantity } from "@visionx/money";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, useParams } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { products } from "../api/endpoints.catalogue";
import { adjustments, serials, stock, type AdjustmentBody } from "../api/endpoints.inventory";
import { carriedOut, describeError, isApiError } from "../api/problem";
import { formatDate, formatDateTime, formatMoney } from "../lib/format";
import { centsFromText, textFromCents } from "../lib/money-input";
import { can, useSessionView } from "../session/store";
import { fieldScanCollector } from "../shell/key-burst";
import { useStepUp } from "../stepup/StepUpProvider";
import { Alert, Checkbox, Chip, Empty, ErrorAlert, Field, KV, Loading, LockMark, Modal, Pager, Panel, Select, TextArea, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import { SERIAL_WORD } from "./CatalogueShared";
import { NoAccessPanel, PageHead } from "./common";
import { CameraScan, ControlLogRef, DocumentRef, personWithId, signed, useActingBranch, useStockBranch, useStockWrites } from "./StockShared";

export const REASON_WORD: Readonly<Record<AdjustmentReason, string>> = STOCK_MOVEMENT_REASON_LABELS;
/** What became of a line's units, in the dialog's words (BR-INVENTORY-013). */
export const RESULT_WORD: Readonly<Record<AdjustmentLineResult, string>> = {
  Increase: "added", WriteOff: "written off", Quarantine: "kept as damaged", Release: "released to stock", ReturnToSupplier: "returned to the supplier", Correction: "corrected",
};
/** The change a new line starts at: a reason that lowers stock a unit out, a correction or Other a unit in. */
const STARTS_OUT: ReadonlySet<AdjustmentReason> = new Set(["Damaged", "LostTheft", "ReturnedToSupplier", "DisplaySample"]);

interface Line {
  key: string;
  productId: string; sku: string; name: string; trackSerials: boolean;
  serialId: string | null; serialNumber: string | null; serialStatus: SerialStatus | null;
  /** A quantity line's change as typed (`+2`, `-1`); a serial's is ±1. */
  change: string;
  /** A Damaged serial: released to stock (+1) or written off (−1). */
  release: boolean;
  costText: string;
  averageCents: number | null;
}

let lineNo = 0;
/** A typed change, `+2` or `−1`; anything else — or more digits than a change can have — is none (the server answers the range). */
const parseChange = (t: string): number | null => { const s = t.trim().replace(/^\+/, "").replace(/^[−–]/, "-"); return /^-?\d{1,6}$/.test(s) ? Number(s) : null; };

/** A line's change, result and unit cost as the document would post it. */
function planOf(l: Line, reason: AdjustmentReason | "", quarantine: boolean): { delta: number | null; result: AdjustmentLineResult; unit: number | null } {
  if (l.serialId) {
    if (l.serialStatus === "Damaged") return l.release ? { delta: 1, result: "Release", unit: l.averageCents } : { delta: -1, result: "WriteOff", unit: l.averageCents };
    if (reason === "Damaged" && quarantine) return { delta: -1, result: "Quarantine", unit: l.averageCents };
    return { delta: -1, result: decreaseResult(reason), unit: l.averageCents };
  }
  const delta = parseChange(l.change);
  if (delta !== null && delta > 0) return { delta, result: "Increase", unit: centsFromText(l.costText) };
  return { delta, result: decreaseResult(reason), unit: l.averageCents };
}
const decreaseResult = (reason: AdjustmentReason | ""): AdjustmentLineResult =>
  reason === "Damaged" || reason === "LostTheft" ? "WriteOff" : reason === "ReturnedToSupplier" ? "ReturnToSupplier" : "Correction";

/** The refusal shown under a line, in words, from the server's answer (UC-INVENTORY-006 E1–E3). */
function lineRefusal(error: unknown, l: Line, index: number): string | null {
  if (!isApiError(error)) return null;
  const m = error.meta;
  if (error.code === "INSUFFICIENT_STOCK" && m.productId === l.productId && !l.serialId) return `Only ${String(m.available ?? 0)} in store`;
  if (error.code === "SERIAL_UNAVAILABLE" && m.serialId === l.serialId) {
    const st = typeof m.status === "string" && m.status in SERIAL_WORD ? SERIAL_WORD[m.status as SerialStatus].toLowerCase() : "not in store";
    return `${l.serialNumber ?? "This serial"} is not available: ${st}${typeof m.heldByOrderId === "string" ? ` for ${m.heldByOrderId}` : ""}`;
  }
  if (error.code === "ILLEGAL_TRANSITION" && l.serialId && l.release) return `${l.serialNumber} is not damaged: only a quarantined frame is released`;
  if (error.code === "VALIDATION_FAILED") {
    const f = error.errors.find((e) => e.path.startsWith(`lines.${index}.`) || e.path === `lines.${index}`);
    if (!f) return null;
    if (f.path.endsWith("unitCostCents")) return f.code === "required" ? "An increase needs its unit cost ex VAT" : "A cost from R 0.00 to R 999 999.99";
    if (f.path.endsWith("quantityDelta")) return "A whole change from −9 999 to +9 999, never 0";
    if (f.path.endsWith("serialId")) return f.code === "duplicate" ? "This serial is on the document twice" : f.code === "required" ? "A decrease of a serialised product names its serial: scan it" : f.message;
    if (f.path.endsWith("productId")) return f.code === "not_stocked" ? "This product holds no stock" : f.message;
    return f.message;
  }
  return null;
}

const dash = <span className="faint">—</span>;

type NewLine = { productId: string; sku: string; name: string; trackSerials: boolean; averageCents: number | null };
const lineOfStock = (l: StockLineView): NewLine => ({ productId: l.productId, sku: l.sku, name: l.name, trackSerials: l.trackSerials, averageCents: l.onHand > 0 || l.averageCostCents > 0 ? l.averageCostCents : null });
/** A quantity line: a unit out for a reason that lowers stock, a unit in for a correction, Other, or a serialised product found. */
const newLine = (p: NewLine, reason: AdjustmentReason | ""): Line => ({
  key: `l${++lineNo}`, ...p, serialId: null, serialNumber: null, serialStatus: null, release: false, costText: textFromCents(p.averageCents),
  change: p.trackSerials || (reason !== "" && !STARTS_OUT.has(reason)) ? "+1" : "-1",
});

/** UC-INVENTORY-006: the Adjust dialog, at the acting branch; `initial` starts it on a line selected on Stock. */
/**
 * `onDone` takes the adjustment posted — or null when it was posted and its answer not kept (409 REQUEST_COMPLETED, UC-INVENTORY-006
 * A10): the page says it was posted and where it is listed.
 */
export function AdjustModal({ initial, scanFirst, onClose, onDone }: { initial: StockLineView | null; scanFirst?: string | undefined; onClose: () => void; onDone: (a: AdjustmentView | null) => void }) {
  const withStepUp = useStepUp();
  const acting = useActingBranch();
  const { online, title } = useStockWrites();
  const [reason, setReason] = useState<AdjustmentReason | "">("");
  const [quarantine, setQuarantine] = useState(false);
  const [note, setNote] = useState("");
  // A quantity line selected on Stock starts the dialog; for a serialised product the scan box waits for its serial.
  const [lines, setLines] = useState<Line[]>(() => (initial && !initial.trackSerials ? [newLine(lineOfStock(initial), "")] : []));
  const [scan, setScan] = useState("");
  const [scanMsg, setScanMsg] = useState<ReactNode>(null);
  const [choices, setChoices] = useState<StockLineView[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const scanRef = useRef<HTMLInputElement>(null);

  const addQuantityLine = (p: NewLine) => setLines((ls) => [...ls, newLine(p, reason)]);
  const fromLine = (l: StockLineView) => addQuantityLine(lineOfStock(l));

  // UC-INVENTORY-006 step 2: a serial (Available — or Damaged, to release or write off), else a pack barcode or SKU at the branch,
  // else a product of the catalogue the branch has not held yet (an increase brings its first units).
  const resolve = async (code: string) => {
    const c = code.trim();
    if (!c) return;
    setScanMsg(null);
    setChoices(null);
    try {
      const found = (await stock.list({ q: c, branchId: acting.id ?? undefined }, { limit: 10 })).data.items;
      const hit = found.length === 1 ? found[0]! : null;
      if (hit?.matchedSerial) {
        const s = (await serials.list({ q: hit.matchedSerial, branchId: acting.id ?? undefined }, { limit: 2 })).data.items.find((x) => x.serialNumber === hit.matchedSerial);
        if (!s) { setScanMsg(<>Serial <span className="mono">{hit.matchedSerial}</span> is not at {acting.name}.</>); return; }
        if (lines.some((l) => l.serialId === s.id)) { setScanMsg(<><span className="mono">{s.serialNumber}</span> is on the document already.</>); return; }
        if (s.status !== "Available" && s.status !== "Damaged") { setScanMsg(<><span className="mono">{s.serialNumber}</span> is not available: {SERIAL_WORD[s.status].toLowerCase()}{s.reservedOrderId && s.status === "Reserved" ? ` for ${s.reservedOrderId}` : ""}.</>); return; }
        setLines((ls) => [...ls, {
          key: `l${++lineNo}`, productId: hit.productId, sku: hit.sku, name: hit.name, trackSerials: true, serialId: s.id, serialNumber: s.serialNumber, serialStatus: s.status,
          change: "-1", release: false, costText: "", averageCents: hit.averageCostCents,
        }]);
      } else if (hit) fromLine(hit);
      else if (found.length > 1) setChoices(found);
      else {
        const lookup = await products.lookup(c).catch(() => null);
        const p: ProductSummary | undefined = lookup?.data.items.find((i) => i.serial === null && i.product.stocked && !i.product.isComposite)?.product;
        if (p) addQuantityLine({ productId: p.id, sku: p.sku, name: p.displayName, trackSerials: p.trackSerials, averageCents: null });
        else setScanMsg(<>Nothing at {acting.name} matches <span className="mono">{c}</span>.</>);
      }
      setScan("");
    } catch (err) { setScanMsg(describeError(err)); }
    finally { scanRef.current?.focus(); }
  };

  // Opened from a serial's page: that serial is the first line.
  useEffect(() => { if (scanFirst) void resolve(scanFirst); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  /** A code read by the scanner or the camera, looked up as if scanned into Lines. */
  const scanned = (code: string) => { setScan(code); void resolve(code); };
  // A scanner's code that lands in a line's Change or Unit cost — the cursor left there after typing a figure — is a scan (A12): the
  // box goes back to what it held before the code began, the code is looked up as if scanned into Lines, and its Enter posts
  // nothing. A person's keys, slower, act as ever, Enter posting the document (the completeness audit of 23 Oct 2026;
  // TC-INVENTORY-169; the pattern of Receive stock's TC-PURCHASING-125).
  const collectors = useRef(new Map<string, ReturnType<typeof fieldScanCollector>>());
  const figureKeys = (key: string, field: "change" | "cost") => (e: KeyboardEvent<HTMLInputElement>) => {
    const id = `${key}:${field}`;
    const c = collectors.current.get(id) ?? fieldScanCollector();
    collectors.current.set(id, c);
    const hit = c.take(e.nativeEvent, e.currentTarget.value);
    if (!hit) return;
    e.preventDefault();
    e.stopPropagation();
    setLines((ls) => ls.map((x) => (x.key !== key ? x : field === "change" ? { ...x, change: hit.before } : { ...x, costText: hit.before })));
    scanned(hit.code);
  };

  const plans = lines.map((l) => planOf(l, reason, quarantine));
  const values: Cents[] = plans.map((p) => (p.delta === null || p.unit === null || p.result === "Quarantine" || p.result === "Release" ? cents(0) : timesQuantity(cents(p.unit), p.delta)));
  const total = sumCents(values);
  const lowers = plans.some((p) => (p.delta !== null && p.delta < 0) || p.result === "Quarantine");
  const body: AdjustmentBody | null = reason === "" ? null : {
    reason, note: note.trim() || undefined,
    lines: lines.map((l, i) => {
      const p = plans[i]!;
      return {
        productId: l.productId, ...(l.serialId ? { serialId: l.serialId } : {}), quantityDelta: p.delta ?? 0,
        ...(p.result === "Quarantine" || p.result === "Release" || (p.result === "WriteOff" && l.serialStatus === "Damaged") ? { outcome: p.result === "Quarantine" ? "Quarantine" as const : p.result === "Release" ? "Release" as const : "WriteOff" as const } : {}),
        ...(p.result === "Increase" && p.unit !== null ? { unitCostCents: p.unit } : {}),
      };
    }),
  };
  const bodyText = JSON.stringify(body);
  // One key per document as it stands: the same document retried after a lost answer posts once (A10).
  const key = useMemo(() => newIdempotencyKey(), [bodyText]);
  const [reasonErr, setReasonErr] = useState<string | null>(null);

  const post = async () => {
    if (busy) return;
    if (!body) { setReasonErr("Choose a reason"); return; }
    if (lines.length === 0) { setError(new Error("Add a line: scan a serial or a barcode, or type an SKU.")); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await withStepUp((g) => adjustments.post(body, { ...g, idempotencyKey: key }), {
        title: "Adjust stock", reason: "none",
        description: `${lines.length} line${lines.length === 1 ? "" : "s"} at ${acting.name}, ${formatMoney(total)} at cost. It lowers stock: an authoriser's Control PIN is needed, and the note is written to the Control Log.`,
      });
      if (r) onDone(r.data);
    } catch (err) {
      // Posted again after a lost answer and answered as carried out: posted once, its answer not kept — done (the verification of round 3).
      if (carriedOut(err)) onDone(null);
      else setError(err);
    } finally { setBusy(false); }
  };

  const noteErr = isApiError(error) ? error.field("note") : undefined;
  const reasonApiErr = isApiError(error) ? error.field("reason") : undefined;
  const placed = lines.some((l, i) => lineRefusal(error, l, i) !== null) || !!noteErr || !!reasonApiErr;
  return (
    <Modal wide className="adjust" title="Adjust stock" meta={`${acting.name} · numbered ADJ-${acting.code || "…"}-… when posted`} onClose={() => { if (!busy) onClose(); }} onSubmit={() => void post()} footer={<>
      <span className="small faint">Total <span className="mono strong">{formatMoney(total)}</span> at cost</span>
      <span className="right row c gap-8">
        <button className="btn" type="button" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn primary" type="submit" disabled={busy || !online} title={title}>{busy ? "Posting…" : "Post"}{lowers && <LockMark />}</button>
      </span>
    </>}>
      {error !== null && !placed && <Alert tone="crit"><b>Not posted.</b> {describeError(error)}</Alert>}
      <div className="row">
        <Field label="Reason" required htmlFor="adjust-reason" error={reasonErr ?? (reasonApiErr ? (reasonApiErr.code === "not_allowed" ? "A stock-take variance is posted only by a finalised count" : "Choose a reason") : undefined)}>
          <Select id="adjust-reason" value={reason} onChange={(e) => { setReason(e.target.value as AdjustmentReason | ""); setReasonErr(null); if (e.target.value !== "Damaged") setQuarantine(false); }}>
            <option value="">Choose a reason</option>
            {ADJUSTMENT_REASONS.map((r) => <option key={r} value={r}>{REASON_WORD[r]}</option>)}
          </Select>
        </Field>
        {reason === "Damaged" && <div className="field"><label>&nbsp;</label><Checkbox checked={quarantine} onChange={setQuarantine}>Keep as damaged (quarantine)</Checkbox></div>}
      </div>
      <div className="field">
        <label htmlFor="adjust-scan">Lines</label>
        <div className="row c gap-8">
          <div className="input grow">
            <Icon name="barcode" />
            <input id="adjust-scan" ref={scanRef} autoComplete="off" placeholder="Scan a serial or barcode, or type an SKU, and press Enter" value={scan} maxLength={100}
              onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); void resolve(scan); } }} autoFocus={initial?.trackSerials ?? false} />
          </div>
          <CameraScan testId="adjust-camera" onCode={scanned} onMiss={(words) => setScanMsg(words)} />
        </div>
        {scanMsg && <div className="error" role="alert">{scanMsg}</div>}
        {!scanMsg && initial?.trackSerials && lines.length === 0 && <div className="help">{initial.name} is serialised: scan the frame&#39;s serial to take it out; its SKU adds a unit found without one.</div>}
      </div>
      {choices && (
        <div className="list bordered" role="listbox" aria-label="Matching lines">
          {choices.map((c) => <div key={c.productId} className="item"><div className="grow"><span className="mono">{c.sku}</span> {c.name} <span className="small faint">· {c.available} available</span></div><button className="btn sm" type="button" onClick={() => { setChoices(null); fromLine(c); setScan(""); }}>Add</button></div>)}
        </div>
      )}
      {lines.length > 0 && (
        <table className="adjust-lines">
          <thead><tr><th>Product</th><th className="r">Change</th><th>Becomes</th><th className="r">Unit cost</th><th className="r">Value</th><th></th></tr></thead>
          <tbody>
            {lines.map((l, i) => {
              const p = plans[i]!;
              const refusal = lineRefusal(error, l, i);
              return (
                <tr key={l.key}>
                  <td className="product">{l.name}<div className="small faint"><span className="mono nw">{l.sku}</span>{l.serialNumber && <> · <span className="mono nw">{l.serialNumber}</span></>}</div>{refusal && <div className="error" role="alert">{refusal}</div>}</td>
                  <td className="r num nw">{l.serialId ? signed(p.delta ?? 0) : <TextInput aria-label={`Change of ${l.sku}`} boxClass="w-80" inputMode="numeric" value={l.change} onChange={(e) => { const v = e.target.value; setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, change: v } : x))); }} onKeyDown={figureKeys(l.key, "change")} />}</td>
                  <td>{l.serialStatus === "Damaged"
                    ? <Select aria-label={`What becomes of ${l.serialNumber}`} value={l.release ? "release" : "writeoff"} onChange={(e) => { const r = e.target.value === "release"; setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, release: r } : x))); }}>
                        <option value="writeoff">written off</option><option value="release">released to stock</option>
                      </Select>
                    : RESULT_WORD[p.result]}</td>
                  <td className="money">{p.result === "Increase"
                    ? <TextInput aria-label={`Unit cost of ${l.sku}`} boxClass="w-120" mono inputMode="decimal" value={l.costText} placeholder="Cost ex VAT" onChange={(e) => { const v = e.target.value; setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, costText: v } : x))); }} onKeyDown={figureKeys(l.key, "cost")} />
                    : p.unit === null ? dash : <>{formatMoney(p.unit)}<div className="small faint">{acting.name} weighted average</div></>}</td>
                  <td className="money">{formatMoney(values[i] ?? 0)}</td>
                  <td className="r"><button className="btn sm ghost" type="button" onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>Remove</button></td>
                </tr>
              );
            })}
          </tbody>
          <tfoot><tr><td colSpan={4}>{lines.length} line{lines.length === 1 ? "" : "s"}</td><td className="money strong">{formatMoney(total)}</td><td></td></tr></tfoot>
        </table>
      )}
      <Field label="Note" required={lowers || reason === "Other"} htmlFor="adjust-note" error={noteErr ? (reason === "Other" && !lowers ? "Other needs a note" : "A note is required for a decrease") : undefined}
        help={lowers ? "Written to the Control Log as the reason the authoriser sees" : "What happened, in a few words"}>
        <TextArea id="adjust-note" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      {lowers
        ? <div className="help"><LockMark>Requires Control PIN</LockMark> — this lowers stock. An authoriser enters their Control ID and PIN; one Control Log row covers the document.</div>
        : lines.length > 0 && <div className="help">No Control PIN — nothing here lowers stock. The adjustment is recorded in the audit log.</div>}
    </Modal>
  );
}

// --- the branch's adjustments (A11) -----------------------------------------------------------------------------------------

/** /stock/adjustments: the branch's adjustments, newest first (`inventory.adjustment.write`, which has no read twin). */
export function Adjustments() {
  const s = useSessionView();
  const br = useStockBranch();
  const allowed = can(s, "inventory.adjustment.write");
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const q = useQuery({ queryKey: ["stock", "adjustments", br.branchId, cursor ?? ""], queryFn: () => adjustments.list(br.branchId ?? undefined, { cursor }), enabled: allowed && br.branchId !== null, placeholderData: keepPreviousData });
  const rows = q.data?.data.items ?? [];
  return (
    <section className="screen" id="s-stock-adjustments">
      <div className="content">
        <PageHead title={`Adjustments — ${br.name}`} sub="Every adjustment posted at the branch · newest first · a posted adjustment is never changed; a later one corrects it" actions={<Link className="btn" to="/stock">Back to stock</Link>} />
        {!allowed ? <NoAccessPanel page="Adjustments" permission="inventory.adjustment.write" /> : <>
          {br.choices.length > 1 && <div className="toolbar"><Select aria-label="Branch" value={br.branchId ?? ""} onChange={(e) => { setCursors([]); br.choose(e.target.value); }}>{br.choices.map((b) => <option key={b.id} value={b.id}>Branch · {b.name}</option>)}</Select></div>}
          {q.isPending && <Loading what="adjustments" />}
          {q.isError && <ErrorAlert error={q.error} />}
          {q.data && (
            <Panel tight foot={(cursors.length > 0 || q.data.data.nextCursor) ? <Pager label={`Page ${cursors.length + 1}`} hasPrev={cursors.length > 0} hasNext={!!q.data.data.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, q.data.data.nextCursor!])} /> : undefined}>
              {rows.length === 0 ? <Empty title="No adjustments yet." /> : (
                <div className="tbl-scroll">
                  <table>
                    <thead><tr><th>Adjustment</th>{br.isAll && <th>Branch</th>}<th>Posted</th><th>Reason</th><th className="r">Lines</th><th className="r">Value at cost</th><th>Posted by</th><th>Authorised</th></tr></thead>
                    <tbody>{rows.map((a) => (
                      <tr key={a.id}>
                        <td className="mono nw"><Link to={`/stock/adjustments/${a.id}`}>{a.number}</Link></td>{br.isAll && <td>{a.branchName}</td>}
                        <td className="nw">{formatDateTime(a.postedAt)}</td><td>{a.reasonLabel}</td><td className="r num">{a.lineCount}</td><td className="money">{formatMoney(a.totalValueCents)}</td>
                        <td className="nw">{personWithId(a.postedBy)}</td>
                        <td className="nw">{a.authorisedBy ? <>{personWithId(a.authorisedBy)} · <ControlLogRef /></> : <span className="faint">no Control PIN</span>}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </Panel>
          )}
        </>}
      </div>
    </section>
  );
}

/** /stock/adjustments/:id: one adjustment, its note and its lines with what became of each unit (A11). */
export function AdjustmentRecord() {
  const s = useSessionView();
  const { id = "" } = useParams();
  const allowed = can(s, "inventory.adjustment.write");
  const q = useQuery({ queryKey: ["stock", "adjustment", id], queryFn: () => adjustments.get(id), enabled: allowed });
  const a = q.data?.data;
  return (
    <section className="screen" id="s-stock-adjustment">
      <div className="content">
        <PageHead title={a ? <span className="row c gap-8">Adjustment <span className="mono">{a.number}</span><Chip tone="ok" dot>Posted</Chip></span> : "Adjustment"}
          sub={a ? `${a.branchName} · ${a.reasonLabel} · posted ${formatDateTime(a.postedAt)} by ${personWithId(a.postedBy)}` : undefined}
          actions={<Link className="btn" to="/stock/adjustments">All adjustments</Link>} />
        {!allowed && <NoAccessPanel page="Adjustments" permission="inventory.adjustment.write" />}
        {allowed && q.isPending && <Loading what="the adjustment" />}
        {q.isError && (isApiError(q.error) && q.error.code === "NOT_FOUND" ? <Alert tone="warn"><b>This adjustment was not found.</b> It may be another branch's.</Alert> : <ErrorAlert error={q.error} />)}
        {a && <>
          <Panel title="Details">
            <KV rows={[
              ["Reason", a.reasonLabel], ["Note", a.note ?? dash], ["Value at cost", <span className="mono">{formatMoney(a.totalValueCents)}</span>],
              ["Authorised", a.authorisedBy ? <>{personWithId(a.authorisedBy)} · Control PIN · <ControlLogRef /></> : "No Control PIN — nothing lowered stock"],
            ]} />
          </Panel>
          <Panel title="Lines" meta={`${a.lineCount} line${a.lineCount === 1 ? "" : "s"}`} tight>
            <table>
              <thead><tr><th>Product</th><th>Serial</th><th className="r">Change</th><th>Became</th><th className="r">Unit cost</th><th className="r">Value</th></tr></thead>
              <tbody>{a.lines.map((l) => (
                <tr key={l.id}><td>{l.productName}<div className="small faint mono">{l.sku}</div></td><td className="mono">{l.serialNumber ?? dash}</td><td className="r num">{signed(l.quantityDelta)}</td>
                  <td>{RESULT_WORD[l.outcome]}</td><td className="money">{formatMoney(l.unitCostCents)}</td><td className="money">{formatMoney(l.valueCents)}</td></tr>
              ))}</tbody>
              <tfoot><tr><td colSpan={5}>Total at cost</td><td className="money strong">{formatMoney(a.totalValueCents)}</td></tr></tfoot>
            </table>
          </Panel>
          <div className="small faint">Posted {formatDate(a.postedAt)} as stock movements of <DocumentRef doc={{ type: "StockAdjustment", id: null, number: a.number }} /> · stock is valued in Vision X — nothing posts to Zoho Books.</div>
        </>}
      </div>
    </section>
  );
}
